//src/components/atomic-crm/providers/supabase/authProvider.ts
/* eslint-disable no-var */
/* eslint-disable @typescript-eslint/no-namespace */
import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { supabase } from "./supabase";
import { clearOrgIdCache } from "./data-provider/handlers/create";


const baseAuthProvider = supabaseAuthProvider(supabase, {
  getIdentity: async () => {
    const sale = await getSaleFromCache();

    if (sale == null) {
      throw new Error();
    }

    // Fetch org onboarding status
    const { data: org } = await supabase
      .from("organizations")
      .select("onboarding_completed_at")
      .eq("id", sale.org_id)
      .maybeSingle();

    return {
      id: sale.id,
      fullName: `${sale.first_name} ${sale.last_name}`,
      avatar: sale.avatar?.src,
      locale: sale.locale ?? undefined,
      administrator: sale.administrator,
      org_id: sale.org_id, // 👈 Make sure this is returned
      onboarding_completed_at: org?.onboarding_completed_at ?? null,
    };
  }
});

export async function getIsInitialized() {
  if (getIsInitialized._is_initialized_cache == null) {
    const { data } = await supabase.from("init_state").select("is_initialized");

    getIsInitialized._is_initialized_cache = data?.at(0)?.is_initialized > 0;
  }

  return getIsInitialized._is_initialized_cache;
}

export namespace getIsInitialized {
  export var _is_initialized_cache: boolean | null = null;

  // Helper to clear cache (useful after migrations or setup)
  export function clearCache() {
    _is_initialized_cache = null;
  }
}

export const authProvider: AuthProvider = {
  ...baseAuthProvider,
  login: async (params) => {
    const result = await baseAuthProvider.login(params);
    // clear cached sale
    cachedSale = undefined;
    clearOrgIdCache(); // 👈 new
    return result;
  },
  logout: async (params) => {
    const result = await baseAuthProvider.logout?.(params);
    cachedSale = undefined;
    clearOrgIdCache(); // 👈 new
    return result;
  },
  checkAuth: async (params) => {
    // 1. Catch the password reset query string BEFORE React Admin renders the Dashboard
    if (
      typeof window !== "undefined" &&
      window.location.search.includes("mode=set-password")
    ) {
      // Clean the URL so we don't loop
      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + window.location.hash,
      );

      // Throw a redirect. React Admin will catch this and route the user.
      throw {
        redirectTo: "/set-password",
        message: false,
      } as any;
    }

    // Users are on the set-password page, nothing to do
    if (
      window.location.pathname === "/set-password" ||
      window.location.hash.includes("#/set-password")
    ) {
      return;
    }
    // Users are on the forgot-password page, nothing to do
    if (
      window.location.pathname === "/forgot-password" ||
      window.location.hash.includes("#/forgot-password")
    ) {
      return;
    }
    // Users are on the change-password page, nothing to do
    if (
      window.location.pathname === "/change-password" ||
      window.location.hash.includes("#/change-password")
    ) {
      return;
    }
    // Users are on the otp-login page, nothing to do
    if (
      window.location.pathname === "/otp-login" ||
      window.location.hash.includes("#/otp-login")
    ) {
      return;
    }
    // Users are on the sign-up page, nothing to do
    if (
      window.location.pathname === "/sign-up" ||
      window.location.hash.includes("#/sign-up")
    ) {
      return;
    }

    const isInitialized = await getIsInitialized();

    if (!isInitialized) {
      await supabase.auth.signOut();
      throw {
        redirectTo: "/sign-up",
        message: false,
      };
    }

    return baseAuthProvider.checkAuth(params);
  },
  canAccess: async (params) => {
    const isInitialized = await getIsInitialized();
    if (!isInitialized) return false;

    // Get the current user
    const sale = await getSaleFromCache();
    if (sale == null) return false;

    // Compute access rights from the sale role
    const role = sale.administrator ? "admin" : "user";
    return canAccess(role, params);
  },

  // ✅ Harden against false logouts
  checkError: async (error: any) => {
    const status = error?.status ?? error?.body?.code ?? error?.code;

    // 401 = session truly invalid → log out
    if (status === 401) {
      return Promise.reject();
    }

    // 403 = resource forbidden, user IS still authenticated
    if (status === 403) {
      return Promise.resolve();
    }

    // 5xx = server problem; never log out
    if (typeof status === "number" && status >= 500) {
      return Promise.resolve();
    }

    // Everything else (network errors, 404s, etc.) — stay logged in
    return Promise.resolve();
  },
};

let cachedSale: any;
let pendingSaleRequest: Promise<any> | null = null;

export const updateCachedSaleLocale = (locale: string) => {
  if (!cachedSale) return;
  cachedSale = {
    ...cachedSale,
    locale,
  };
};

// src/components/atomic-crm/providers/supabase/authProvider.ts

const getSaleFromCache = async () => {
  if (cachedSale != null) return cachedSale;
  if (pendingSaleRequest != null) return pendingSaleRequest;

  pendingSaleRequest = (async () => {
    try {
      const { data: dataSession, error: errorSession } = await supabase.auth.getSession();

      // If there is no session yet, just return undefined gracefully
      if (dataSession?.session?.user == null || errorSession) {
        return undefined;
      }

      const { data: dataSale, error: errorSale } = await supabase
        .from("sales")
        .select("id, first_name, last_name, avatar, administrator, locale, org_id")
        .match({ user_id: dataSession.session.user.id })
        .maybeSingle();

      if (dataSale == null || errorSale) {
        return undefined;
      }

      cachedSale = dataSale;
      return dataSale;
    } finally {
      pendingSaleRequest = null;
    }
  })();

  return pendingSaleRequest;
};
